import { useCallback, useMemo, useState } from "react";

import { PreloadedQuery, usePreloadedQuery } from "react-relay";
import { graphql, VariablesOf } from "relay-runtime";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import UFOSegment from "@atlaskit/react-ufo/segment";

import {
    confluenceTemplatesUnifiedContentfulRelayPageQuery,
    confluenceTemplatesUnifiedContentfulRelayPageQuery$data
} from "@/__generated_contentful__/confluenceTemplatesUnifiedContentfulRelayPageQuery.graphql";
import { useSearchSuggestionsContentfulQuery$data } from "@/__generated_contentful__/useSearchSuggestionsContentfulQuery.graphql";
import { getIcons } from "@/components/confluence/templates/get-icons";
import { TemplateSectionWrapper } from "@/components/confluence/templates/template-section-wrapper";
import { ConfluenceTemplateAnalytics } from "@/components/confluence/templates/util/confluence-templates-analytics";
import ContentNotFoundErrorBoundary from "@/content/utils/content-not-found-error-boundary";
import { UnifiedTemplatesNavigationSection } from "@/libraries/design/patterns/unified-templates-gallery/navigation-section";
import { useLocale } from "@/libraries/web-framework/src/localization/use-locale";
import { SharedHeaderFooterContainer } from "@/templates/software/confluence/templates/confluence-templates-page/src/sections/shared-header-footer-container";
import { partnerTemplatesDataUrlSlugs } from "@/templates/software/confluence/templates/confluence-templates-page/src/test-data/test-data";
import { checkFeatureFlagGate } from "@/utils/feature-flags/check-feature-flag";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";
import { useLocation } from "@/utils/location-provider";

import {
    AdditionalSlugs,
    CategoryOrder,
    DisplayedSlugs
} from "../../config/category-template-order";
import { TemplateCategories } from "../components/contentful-relay-components/template-categories-contentful-relay";
import { CategorySearchSuggestion } from "../components/contentful-relay-components/template-category-search-results-contentful-relay";
import { TemplateSearchSuggestion } from "../components/contentful-relay-components/template-fuzzy-search-results-contentful-relay";
import { parseSearchQuery } from "../components/contentful-relay-components/template-initial-search-results-contentful-relay";
import {
    DEFAULT_CATEGORY_SLUG,
    parseCategorySlug,
    useCategorySlug
} from "../hooks/use-category-slug";
import { parseQueryLocale } from "../hooks/use-query-locale";
import {
    SectionDivider,
    TemplateCarouselSection
} from "../sections/contentful-relay-sections/template-carousel-section-contentful-relay";
import { TemplateFuzzySearch } from "../sections/contentful-relay-sections/template-fuzzy-search-contentful-relay";
import { TemplateHeroSection } from "../sections/contentful-relay-sections/template-hero-section-contentful-relay";
import { TemplateLearnFromLeadersSection } from "../sections/contentful-relay-sections/template-learn-from-leaders-section-contentful-relay";
import { TemplateSearchResultsSection } from "../sections/contentful-relay-sections/template-search-results-section-contentful-relay";

const parseIsRecommended = (pathname: string) => {
    return (
        pathname.endsWith("/templates") ||
        pathname.endsWith("/categories/recommended")
    );
};

const parseSearchTerm = (params: string) => {
    const search = new URLSearchParams(params);
    return search.get("term");
};

const slugOrder: { [key: string]: number } = AdditionalSlugs.reduce(
    (
        acc: {
            [key: string]: number;
        },
        slug: string,
        index: number
    ) => {
        acc[slug] = index;
        return acc;
    },
    {}
);

const useAdditionalCategories = (
    categories: confluenceTemplatesUnifiedContentfulRelayPageQuery$data["categories"]
) => {
    const transformedCategories = tranformCategories(categories);
    const templateCategories = useMemo(() => {
        return transformedCategories.edges
            .map(({ node: { context } }) => context)
            .filter((category) =>
                // @ts-expect-error Contentful category context urlSlug is not narrowed by generated Relay type.
                AdditionalSlugs.includes(category.urlSlug)
            )
            .sort(
                (a, b) =>
                    (slugOrder[a.urlSlug] ?? 0) - (slugOrder[b.urlSlug] ?? 0)
            );
    }, [transformedCategories]);
    return templateCategories;
};

const useSortedCategories = (
    categories: confluenceTemplatesUnifiedContentfulRelayPageQuery$data["categories"]
) => {
    const transformedCategories = tranformCategories(categories);
    const adjustedCategories = useMemo(() => {
        return (
            transformedCategories.edges
                .map(({ node: { context } }) => ({
                    ...context,
                    // @ts-expect-error Contentful category context urlSlug is not narrowed by generated Relay type.
                    ...getIcons(context.urlSlug)
                }))
                // @ts-expect-error Contentful category context urlSlug is not narrowed by generated Relay type.
                .filter((category) => DisplayedSlugs.includes(category.urlSlug))
                .sort((a, b) => {
                    const indexA =
                        CategoryOrder[
                            a.urlSlug as keyof typeof CategoryOrder
                        ] ?? Number.MAX_VALUE;
                    const indexB =
                        CategoryOrder[
                            b.urlSlug as keyof typeof CategoryOrder
                        ] ?? Number.MAX_VALUE;
                    return indexA - indexB;
                })
        );
    }, [transformedCategories]);

    return adjustedCategories;
};

export const tranformCategories = (
    categories:
        | confluenceTemplatesUnifiedContentfulRelayPageQuery$data["categories"]
        | useSearchSuggestionsContentfulQuery$data["categories"]
): {
    edges: {
        node: {
            context: {
                id: string;
                urlSlug: string;
                name: string;
                shortDescriptionOneLiner: string;
                experiment: boolean;
            };
        };
    }[];
} => {
    if (!categories?.items) {
        return { edges: [] };
    }

    // First map all items to nodes
    const nodes =
        categories.items
            ?.filter((item) => {
                // Skip if item is null or has no category collection
                if (!item?.categoryCollection?.items) {
                    return false;
                }

                // Filter out null items
                const validItems =
                    item.categoryCollection.items.filter(Boolean);

                // Check if any remaining items have templates
                const hasTemplates = validItems.some((category) => {
                    const total =
                        category?.linkedFrom?.productTemplateCollection
                            ?.total ?? 0;
                    return total > 0;
                });

                return validItems.length > 0 && hasTemplates;
            })
            .flatMap((item) => {
                return (
                    item?.categoryCollection?.items.map((categoryItem) => ({
                        node: {
                            context: {
                                id: categoryItem?.sys?.id || "",
                                urlSlug: categoryItem?.urlSlug || "",
                                name: categoryItem?.categoryNameExternal || "",
                                shortDescriptionOneLiner:
                                    categoryItem?.shortDescriptionOneLiner ||
                                    "",
                                experiment: categoryItem?.experiment || false
                            },
                            field: "category"
                        }
                    })) || []
                );
            }) || [];

    // Then remove duplicates based on urlSlug
    const uniqueNodes = nodes.reduce((acc, node) => {
        const urlSlug = node.node.context.urlSlug;
        if (
            !urlSlug ||
            acc.some((existing) => existing.node.context.urlSlug === urlSlug)
        ) {
            return acc;
        }
        acc.push(node);
        return acc;
    }, [] as typeof nodes);

    return {
        edges: uniqueNodes
    };
};

export const ConfluenceTemplatesUnifiedRelayPageQuery = graphql`
    query confluenceTemplatesUnifiedContentfulRelayPageQuery(
        $locale: String!
        $partnerTemplatesDataUrlSlugs: [String!]!
        $urlSlugs: [String!]!
        $templateIds: [String!]!
        $categoryUrlSlugs: [String!]!
        $limit: Int!
        $skip: Int!
        $isSearch: Boolean = false
    ) {
        collections: templateCollectionCollection(limit: 50, locale: $locale) {
            items {
                ...collectionCardCarouselContentfulRelayFragment
            }
        }
        categories: productTemplateCollection(limit: 400, locale: $locale) {
            items {
                categoryCollection(limit: 10) {
                    total
                    items {
                        sys {
                            id
                        }
                        urlSlug
                        categoryNameExternal
                        shortDescriptionOneLiner
                        experiment
                        linkedFrom(
                            allowedLocales: [
                                "en-US"
                                "es"
                                "ja-JP"
                                "pt-BR"
                                "fr-FR"
                                "de-DE"
                                "nl-NL"
                                "it-IT"
                                "ko-KR"
                                "pl-PL"
                                "ru-RU"
                                "zh-CN"
                            ]
                        ) {
                            productTemplateCollection(limit: 1) {
                                total
                            }
                        }
                    }
                }
            }
        }
        partnerTemplates: productTemplateCollection(
            locale: $locale
            where: { urlSlug_in: $partnerTemplatesDataUrlSlugs }
        ) {
            items {
                ...partnerTemplateCardCarouselContentfulRelayFragment
            }
        }
        ...templatePaginatedSearchResultsContentfulRelayRefetchableFragment
            @skip(if: $isSearch) # Skip preloading if doing a client-side search.
            @alias(as: "preloadedSearchResults")
            @arguments(
                urlSlugs: $urlSlugs
                templateIds: $templateIds
                categoryUrlSlugs: $categoryUrlSlugs
                limit: $limit
                skip: $skip
                locale: $locale
            )
    }
`;

// eslint-disable-next-line complexity
export const getConfluenceTemplatesUnifiedRelayPageQueryVars = (
    location: Location,
    partialVars?: Partial<
        VariablesOf<confluenceTemplatesUnifiedContentfulRelayPageQuery>
    >
    // eslint-disable-next-line complexity
): VariablesOf<confluenceTemplatesUnifiedContentfulRelayPageQuery> => {
    const isSearch =
        partialVars?.isSearch ??
        (parseIsRecommended(location.pathname) ||
            parseSearchTerm(location.search) !== null);

    const locale = partialVars?.locale ?? parseQueryLocale(location);
    const searchQuery = parseSearchQuery(location);
    return {
        locale,
        partnerTemplatesDataUrlSlugs:
            partialVars?.partnerTemplatesDataUrlSlugs ??
            partnerTemplatesDataUrlSlugs,
        limit: searchQuery?.first ?? 9,
        skip: 0,
        urlSlugs: searchQuery?.whereClause?.["urlSlugs"] ?? [],
        templateIds: searchQuery?.whereClause?.["templateIds"] ?? [],
        categoryUrlSlugs: searchQuery?.whereClause?.["categoryUrlSlugs"] ?? [],
        isSearch
    };
};

const getRoute = (
    locale: string,
    searchTerm?:
        | string
        | CategorySearchSuggestion
        | TemplateSearchSuggestion
        | null
) => {
    let prefix = locale === "en" ? "" : `/${locale}`;
    prefix = `${prefix}/software/confluence/templates`;

    if (typeof searchTerm === "string") {
        return `${prefix}/search?term=${searchTerm}`;
    } else if (searchTerm?.type === "template" && searchTerm.name) {
        return `${prefix}/search?term=${searchTerm.name}`;
    } else if (
        searchTerm?.type === "category" &&
        searchTerm.id &&
        searchTerm.id !== DEFAULT_CATEGORY_SLUG
    ) {
        return `${prefix}/categories/${searchTerm.id}`;
    } else {
        return `${prefix}`;
    }
};

export type ConfluenceTemplatesUnifiedRelayPageTemplateProps = {
    queryRef: PreloadedQuery<confluenceTemplatesUnifiedContentfulRelayPageQuery>;
};

export const ConfluenceTemplatesUnifiedRelayPageTemplate = ({
    queryRef
}: ConfluenceTemplatesUnifiedRelayPageTemplateProps) => {
    const location = useLocation();
    const locale = useLocale();

    const categorySlug = useCategorySlug();

    const {
        categories,
        collections,
        partnerTemplates,
        preloadedSearchResults
    } = usePreloadedQuery<confluenceTemplatesUnifiedContentfulRelayPageQuery>(
        ConfluenceTemplatesUnifiedRelayPageQuery,
        queryRef
    );

    const transformedCategories = tranformCategories(categories);
    const categoryName = useMemo(() => {
        if (!categorySlug || categorySlug === DEFAULT_CATEGORY_SLUG) {
            return null;
        }
        const category = transformedCategories.edges.find(
            ({ node: { context } }) => context.urlSlug === categorySlug
        );
        return category?.node.context.name ?? null;
    }, [transformedCategories, categorySlug]);

    const [searchTerm, setSearchTerm] = useState<
        string | CategorySearchSuggestion | TemplateSearchSuggestion | null
    >(() => {
        let term = parseSearchTerm(location.search);
        if (!term) {
            term = parseCategorySlug(location.pathname);
            if (term && term !== DEFAULT_CATEGORY_SLUG) {
                return { type: "category", id: term };
            } else {
                return null;
            }
        }
        return term;
    });

    const { createAnalyticsEvent } = useAnalyticsEvents();

    const handleCategoryChange = useCallback(
        (id: string) => {
            const searchTerm: CategorySearchSuggestion = {
                type: "category",
                id
            };

            setSearchTerm(
                searchTerm.id === DEFAULT_CATEGORY_SLUG ? null : searchTerm
            );
            const url = getRoute(locale, searchTerm);

            history.replaceState(null, "", url);

            createAnalyticsEvent({
                action: "viewed",
                eventType: "screen",
                name: "page",
                attributes: {
                    category: id,
                    page_path: url,
                    originProduct: `wac-software-confluence-templates-categories-${id}`,
                    isMarketingEvent: true
                }
            }).fire();
            ConfluenceTemplateAnalytics.viewTemplatesTaxonomyCategory(id);
        },
        [createAnalyticsEvent, locale]
    );

    const handleSearchSubmit = useCallback(
        (
            searchTerm:
                | string
                | CategorySearchSuggestion
                | TemplateSearchSuggestion
                | null
        ) => {
            setSearchTerm(searchTerm);
            history.replaceState(null, "", getRoute(locale, searchTerm));
        },
        [locale]
    );

    return (
        <div data-testid="confluence-templates-relay-page">
            <UFOSegment name="confluence-templates-relay">
                <SharedHeaderFooterContainer>
                    <ContentNotFoundErrorBoundary>
                        {/* @ts-expect-error TemplateHeroSection children type does not include TemplateFuzzySearch. */}
                        <TemplateHeroSection categoryName={categoryName}>
                            <TemplateFuzzySearch
                                onSubmit={handleSearchSubmit}
                                value={
                                    typeof searchTerm === "string"
                                        ? searchTerm
                                        : null
                                }
                            />
                        </TemplateHeroSection>
                        <TemplateSectionWrapper columnOrientation>
                            <TemplateCategories
                                categories={useSortedCategories(categories)}
                                onCategoryChange={handleCategoryChange}
                            />
                            <TemplateSearchResultsSection
                                preloadedSearchResults={preloadedSearchResults}
                                searchTerm={searchTerm}
                            />
                        </TemplateSectionWrapper>
                        <SectionDivider />
                        <TemplateCarouselSection
                            collections={
                                collections?.items?.filter(
                                    (item): item is NonNullable<typeof item> =>
                                        item !== null
                                ) ?? []
                            }
                        />
                        {checkFeatureFlagGate(
                            featureFlags.wacUnifiedTemplatesNavigation
                        ) && (
                            <UnifiedTemplatesNavigationSection product="confluence" />
                        )}
                        <TemplateLearnFromLeadersSection
                            templates={
                                partnerTemplates?.items?.filter(
                                    (item): item is NonNullable<typeof item> =>
                                        item !== null
                                ) ?? []
                            }
                            categories={useAdditionalCategories(categories)}
                        />
                    </ContentNotFoundErrorBoundary>
                </SharedHeaderFooterContainer>
            </UFOSegment>
        </div>
    );
};
